import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** 経過時間と滞留アラートの更新間隔。確定済み画面と同じ10秒。 */
const TICK_MS = 10_000;

interface ClockValue {
  /** 画面が使う現在時刻。サーバ時刻との差を補正したもの。 */
  now: number;
  /** デモ用の時刻送り。検証以外では0のまま。 */
  demoOffsetMs: number;
  advanceDemo(ms: number): void;
  syncServerTime(serverTime: number): void;
}

const ClockContext = createContext<ClockValue | null>(null);

/**
 * 時刻の基準をサーバに合わせる。
 * 端末ごとの時計ずれで「経過◯分」が食い違わないようにするため、
 * 状態取得のたびにサーバ時刻との差を取り直す。
 */
export function ClockProvider({ children }: { children: ReactNode }) {
  const [tick, setTick] = useState(0);
  const [demoOffsetMs, setDemoOffsetMs] = useState(0);
  const serverOffset = useRef(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const syncServerTime = useCallback((serverTime: number) => {
    const next = serverTime - Date.now();
    // 数百ミリ秒の揺れで再描画しない
    if (Math.abs(next - serverOffset.current) > 1000) {
      serverOffset.current = next;
      setTick((t) => t + 1);
    }
  }, []);

  const advanceDemo = useCallback((ms: number) => setDemoOffsetMs((v) => v + ms), []);

  const value = useMemo<ClockValue>(
    () => ({
      now: Date.now() + serverOffset.current + demoOffsetMs,
      demoOffsetMs,
      advanceDemo,
      syncServerTime,
    }),
    // tick は10秒ごとの再計算を起こすためだけの依存。値そのものは使わない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tick, demoOffsetMs, advanceDemo, syncServerTime],
  );

  return <ClockContext.Provider value={value}>{children}</ClockContext.Provider>;
}

export function useClock(): ClockValue {
  const ctx = useContext(ClockContext);
  if (!ctx) throw new Error('ClockProvider の外で useClock は使えません');
  return ctx;
}
